import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import { Input } from "@/components/@custom/Input";
import { useSession } from "@/hooks/@custom/useSession";
import {
  useGetAttendancesTodayByCompanyQuery,
  useGetSettingsByCompanyQuery,
  useToggleQueueDelayMutation,
  useToggleSettingsToleranceMutation,
  useUpdateAttendancesMutation
} from "@/store/apis/nextcargo";
import { cn } from "@/utils/cn";

function ExpeditionsActionsEnableAttendanceModal({ context, id }: ContextModalProps) {
  const { expedition } = useSession();
  const expeditionCompanyId = expedition?.company.id;

  const { data: attendance } = useGetAttendancesTodayByCompanyQuery({ id: expeditionCompanyId! }, { skip: !expeditionCompanyId });
  const [trigger] = useUpdateAttendancesMutation();

  const handleDimiss = () => {
    context.closeModal(id);
  };

  const handleConfirm = async () => {
    if (!attendance) return;

    try {
      await trigger({ id: attendance.id! });
      handleDimiss();
    } catch {
      toast.error("Erro ao encerrar o atendimento. Tente novamente mais tarde.");
    }
  };

  return (
    <div className="flex flex-col justify-center gap-4">
      <p className="text-center">
        Ao encerrar o atendimento os carros excedentes com previsão de atendimento para hoje serão deslocados para o dia seguinte, deseja concluir o atendimento por hoje?
      </p>

      <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
        <Button variant="outline" color="blue" size="sm" radius="sm" onClick={handleDimiss}>
          Cancelar
        </Button>

        <Button size="sm" radius="sm" onClick={handleConfirm}>
          Confirmar
        </Button>
      </div>
    </div>
  );
}

const schema = z.object({
  delayId: z.string().min(1, "O campo motivo de atraso é obrigatório"),
  time: z.string().min(1, "O campo de tempo é obrigatório")
});
type Schema = z.infer<typeof schema>;
function ExpeditionsActionsEnableDelayModal({ context, id }: ContextModalProps) {
  const { expedition } = useSession();
  const expeditionCompanyId = expedition?.company.id;

  const { data: settings } = useGetSettingsByCompanyQuery({ id: expeditionCompanyId! }, { skip: !expeditionCompanyId });
  const [toggleQueueDelayTrigger] = useToggleQueueDelayMutation();

  const {
    formState: { isSubmitting },
    handleSubmit,
    setValue,
    control,
    watch,
    reset
  } = useForm<Schema>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      delayId: settings?.delay[0]?.id.toString() || "",
      time: settings?.delay[0]?.time || "01:00"
    }
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { delayId, time } = data;

    if (!settings) return;
    if (!expeditionCompanyId) return;

    try {
      await toggleQueueDelayTrigger({
        companyId: expeditionCompanyId,
        delayId: Number(delayId),
        time
      });
      handleDimiss();
    } catch {
      // Do nothing
    }
  };

  const handleDimiss = () => {
    context.closeModal(id);
    reset();
  };

  const delayOptions = settings?.delay.map((delay) => ({
    value: delay.id.toString(),
    time: delay.time,
    label: `${delay.id} - ${delay.reason}`
  }));
  const delayId = watch("delayId");

  useEffect(() => {
    if (delayId) {
      const delay = settings?.delay.find((delay) => delay.id.toString() === delayId);
      if (delay) {
        setValue("time", delay.time);
      }
    }
  }, [delayId]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Input.Select
          label="Motivo"
          description="Selecione o motivo do atraso"
          control={control}
          data={delayOptions}
          name="delayId"
          size="sm"
          radius="sm"
          variant="default"
          withAsterisk
          classNames={{
            label: cn("mb-0!")
          }}
        />
        <Input.Time label="Duração" description="Selecione a duração do atraso em horas" control={control} color="yellow" name="time" readOnly withAsterisk /* withDropdown */ />
      </div>

      <div className="grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-2">
        <Button onClick={handleDimiss} size="sm" color="blue" variant="outline" radius="sm">
          Cancelar
        </Button>

        <Button type="submit" size="sm" radius="sm" loading={isSubmitting} disabled={delayOptions?.length === 0}>
          Iniciar
        </Button>
      </div>
    </form>
  );
}

const textModal: Record<"enable" | "disable", string> = {
  enable: "Ao ativar a tolerância veículos com tempo esgotado cairão automaticamente. Deseja reativar a tolerância?",
  disable: "Ao desativar a tolerância, a fila virtual passa a não descartar veículos atrasados automaticamente e, assim, ficando com o processo mais lento. Deseja continuar?"
};
function ExpeditionsActionsEnableToleranceModal({ context, id }: ContextModalProps) {
  const { expedition } = useSession();
  const expeditionCompanyId = expedition?.company.id;

  const { data: settings } = useGetSettingsByCompanyQuery({ id: expeditionCompanyId! }, { skip: !expeditionCompanyId });
  const [trigger, { isLoading }] = useToggleSettingsToleranceMutation();

  const enable = !settings?.enableTolerance;
  const texts = {
    title: enable ? "Ativar Tolerância" : "Desativar Tolerância",
    text: textModal[enable ? "enable" : "disable"],
    enableMessage: enable ? "Ativar" : "Desativar"
  };

  console.log(enable);

  const handleDimiss = () => {
    context.closeModal(id);
  };

  const handleToggleTolerance = async () => {
    if (!expeditionCompanyId) return;

    try {
      await trigger({
        company: expeditionCompanyId,
        tolerance: enable
      });

      handleDimiss();
    } catch {
      //Do Nothing
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-center leading-relaxed text-gray-600">{texts.text}</p>

      <div className="grid grid-cols-1 gap-1 md:grid-cols-2 md:gap-2">
        <Button color="blue" size="sm" radius="sm" variant="outline" onClick={handleDimiss} className="uppercase!" disabled={isLoading}>
          Cancelar
        </Button>

        <Button color="blue" size="sm" radius="sm" className="uppercase!" onClick={handleToggleTolerance} loading={isLoading}>
          {texts.enableMessage}
        </Button>
      </div>
    </div>
  );
}

export const ExpeditionsActionsEnable = {
  attendance: ExpeditionsActionsEnableAttendanceModal,
  tolerance: ExpeditionsActionsEnableToleranceModal,
  delay: ExpeditionsActionsEnableDelayModal
};
